<template>
   <ClientOnly>
      <AgGridVue
         :key="columnsKey"
         ref="gridRef"
         :theme="gridTheme"
         :get-row-id="getRowId"
         :rowData="rows"
         :locale-text="agGridLocale"
         :quickFilterText="serverSide ? undefined : query"
         :overlay-no-rows-template="`<span>${t('info.no-data')}</span>`"
         :overlay-loading-template="`<span>${t('info.loading')}</span>`"
         :columnDefs="computedColumnDefs"
         style="height: 100%"
         :rowSelection="props.selectable !== false ? { mode: 'multiRow' } : undefined"
         :loading="loading"
         :selectionColumnDef="selectionColumnDef"
         :autoSizeStrategy="{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
            columnLimits: [
               { colId: 'propic_url', maxWidth: 80 },
               { colId: 'image_url', maxWidth: 80 },
               rowLink
                  ? {
                       colId: 'ag-Grid-SelectionColumn',
                       minWidth: SELECTION_COLUMN_WITH_LINK_WIDTH,
                       maxWidth: SELECTION_COLUMN_WITH_LINK_WIDTH,
                    }
                  : { colId: 'ag-Grid-SelectionColumn', maxWidth: 50 },
            ],
         }"
         :defaultColDef="defaultColDefComputed"
         @row-clicked="$emit('rowClicked', $event)"
         @selection-changed="$emit('selectionChanged', $event)"
         @columnMoved="$event.finished ? handleColumnMoved() : undefined"
         @cell-editing-stopped="$emit('cellEditingStopped', $event)"
         @sort-changed="handleSortChanged"
         @cell-context-menu="handleCellContextMenu"
      />
   </ClientOnly>
</template>

<script lang="ts" setup generic="T">
import {
   gridColumnSortPreferenceName,
   isStringArray,
   useDashboardPreference,
} from '../../composables/dashboard-preferences'
import type { RowLinkCellParams } from '../../composables/grid/cell-params'
import { resolveRowId } from '../../composables/grid/row-id'
import { gridTheme } from '../../composables/grid/theme'
import { useNotification } from '../../composables/notification'
import RowLinkRenderer from '../grid/RowLinkRenderer.vue'

import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_IT } from '@ag-grid-community/locale'
import type {
   CellContextMenuEvent,
   CellEditingStoppedEvent,
   ColDef,
   GetRowIdParams,
   GridApi,
   RowClickedEvent,
   SelectionChangedEvent,
   SelectionColumnDef,
   SortChangedEvent,
} from 'ag-grid-community'
import { AgGridVue } from 'ag-grid-vue3'
import { computed, ref, watch } from 'vue'

type Props = {
   id: string
   rows?: T[]
   columns: string[]
   defaultHiddenColumns?: (keyof NonNullable<T>)[]
   defaultSortColumns?: (keyof NonNullable<T>)[]
   disableSortColumns?: (keyof NonNullable<T>)[]
   columnsRenderer?: Partial<Record<keyof NonNullable<T>, ColDef<T>>>
   loading?: boolean
   wrapText?: boolean
   serverSide?: boolean
   sortColumn?: string
   sortDirection?: 'asc' | 'desc'
   selectable?: boolean
   rowIdKey?: string
   rowLink?: (row: T) => string | null | undefined
}

type Emit = {
   rowClicked: [RowClickedEvent]
   selectionChanged: [SelectionChangedEvent]
   cellEditingStopped: [CellEditingStoppedEvent]
   sortChanged: [{ column: string | null; direction: 'asc' | 'desc' | null }]
}

const props = withDefaults(defineProps<Props>(), {
   selectable: true,
})
const columnSort = useDashboardPreference<string[]>(
   () => gridColumnSortPreferenceName(props.id),
   () => (props.defaultSortColumns as string[]) || props.columns || [],
   isStringArray
)
const emit = defineEmits<Emit>()

const query = defineModel<string>('query', {
   default: '',
   type: String,
})

const { t, locale } = useI18n()

const gridRef = ref()
const notification = useNotification()

const agGridLocale = computed(() => {
   switch (locale.value) {
      case 'it':
         return AG_GRID_LOCALE_IT
      case 'de':
         return AG_GRID_LOCALE_DE
      case 'en':
         return AG_GRID_LOCALE_EN
   }
})

const handleColumnMoved = () => {
   const api = gridRef.value?.api as GridApi

   columnSort.value = api
      .getAllDisplayedColumns()
      .filter((c) => !c.getColId().includes('ag-Grid-'))
      .map((c) => c.getColId())
}

const handleSortChanged = (event: SortChangedEvent) => {
   if (!props.serverSide) return

   const sortState = event.api
      .getColumnState()
      .find((col) => col.sort !== null && col.sort !== undefined)

   if (sortState) {
      emit('sortChanged', {
         column: sortState.colId,
         direction: sortState.sort as 'asc' | 'desc',
      })
   } else {
      emit('sortChanged', { column: null, direction: null })
   }
}

const handleCellContextMenu = async (event: CellContextMenuEvent) => {
   event.event?.preventDefault()

   const cellValue = event.api.getCellValue({
      rowNode: event.node,
      colKey: event.column,
      useFormatter: true,
   })
   const textToCopy = cellValue == null ? '' : String(cellValue)

   try {
      await navigator.clipboard.writeText(textToCopy)
      notification?.add({
         type: 'success',
         title: t('info.success'),
         description: t('common.copied-to-clipboard'),
      })
   } catch (err) {
      console.error('Failed to copy to clipboard:', err)
   }
}

const defaultColDefComputed = computed<ColDef<T>>(() => ({
   autoHeight: true,
   wrapText: props.wrapText,
   cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: props.wrapText ? 'normal' : 'nowrap',
      lineHeight: '1.5',
   },
   valueFormatter: (params) => (params.value == null ? '-' : String(params.value)),
   sortable: true,
   comparator: props.serverSide ? () => 0 : undefined,
}))

const getRowId = (params: GetRowIdParams<T>) => resolveRowId(params.data, props.rowIdKey)

const columnsKey = computed(() => props.columns.join('|'))

const SELECTION_COLUMN_WITH_LINK_WIDTH = 88

const selectionColumnDef = computed<SelectionColumnDef | undefined>(() => {
   const rowLink = props.rowLink
   if (!rowLink) return undefined
   return {
      width: SELECTION_COLUMN_WITH_LINK_WIDTH,
      minWidth: SELECTION_COLUMN_WITH_LINK_WIDTH,
      maxWidth: SELECTION_COLUMN_WITH_LINK_WIDTH,
      suppressSizeToFit: true,
      cellRenderer: RowLinkRenderer,
      cellRendererParams: (params: { data?: T }): RowLinkCellParams => ({
         link: params.data ? rowLink(params.data) : null,
      }),
   }
})

const EDITABLE_HEADER_CLASS = 'grid-header-editable'

const mergeHeaderClass = (existing: ColDef<T>['headerClass']): ColDef<T>['headerClass'] => {
   if (!existing) return EDITABLE_HEADER_CLASS
   if (typeof existing === 'string') return [existing, EDITABLE_HEADER_CLASS]
   if (Array.isArray(existing)) return [...existing, EDITABLE_HEADER_CLASS]
   return (params) => {
      const result = existing(params)
      if (!result) return EDITABLE_HEADER_CLASS
      return Array.isArray(result)
         ? [...result, EDITABLE_HEADER_CLASS]
         : [result, EDITABLE_HEADER_CLASS]
   }
}

const computedColumnDefs = computed(() => {
   return [
      ...(columnSort.value || []),
      ...props.columns.filter((c) => !(columnSort.value || []).includes(c)),
   ]
      .filter((c) =>
         props.defaultHiddenColumns
            ? !props.defaultHiddenColumns.includes(c as keyof NonNullable<T>)
            : true
      )
      .filter((c) => props.columns.includes(c))
      .map((field) => {
         const columnDef: ColDef<T> = {
            field: field as ColDef<T>['field'],
            headerName: t(`grid.columns.${field as string}`, {}, { default: field }),
            ...props.columnsRenderer?.[field as keyof NonNullable<T>],
         }

         if (columnDef.editable) {
            columnDef.headerClass = mergeHeaderClass(columnDef.headerClass)
         }

         if (props.disableSortColumns?.includes(field as keyof NonNullable<T>)) {
            columnDef.sortable = false
         }

         if (props.serverSide && props.sortColumn === field) {
            columnDef.sort = props.sortDirection || null
         }

         return columnDef
      })
})

watch(
   () => columnSort.value,
   (curr) => {
      const fallback = (props.defaultSortColumns as string[]) || props.columns
      if (curr.length === 0 && fallback?.length) {
         columnSort.value = [...fallback]
      }
   },
   { immediate: true, deep: true }
)

defineExpose({
   getSelectedRows: (): T[] => {
      const api = gridRef.value?.api as GridApi<T> | undefined
      return api?.getSelectedRows() || []
   },
   deselectAll: () => {
      const api = gridRef.value?.api as GridApi<T> | undefined
      api?.deselectAll()
   },
})
</script>
